import type { PoseSnapshot, Route } from '../types';
import { LocalizationState } from '../types';
import { getSummaryLines, exportReport } from '../services/debugLog';
import { getVoiceDebugLine } from '../services/voiceService';
import { setOccluderEnabled, isOccluderEnabled } from '../services/occluderService';
import { isStatsHudVisible, setStatsHudVisible } from '../services/statsHudService';
import { getVersionLine } from '../version';

/**
 * Collapsible dev/debug panel: current pose, tracking state, route nodes,
 * next waypoint. Toggle with the 🐞 button. Updates are throttled by caller.
 */
export class DebugPanel {
  readonly el: HTMLDivElement;
  readonly toggleButton: HTMLButtonElement;
  private body: HTMLPreElement;
  private visible = false;

  private state: LocalizationState = LocalizationState.NotStarted;
  private pose: PoseSnapshot | null = null;
  private route: Route | null = null;
  private nextIdx = 0;
  private lastResultInfo = '';

  constructor(
    /** Applies the toggled preference to the live StatsHud element (owned by main.ts, not this panel). */
    private onToggleStatsHud?: (visible: boolean) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'debug-panel hidden';
    this.body = document.createElement('pre');
    this.el.appendChild(this.body);

    // Export: builds a JSON support report (device info, VPS stats,
    // correction history, route/navmesh events) — share sheet on mobile.
    const exportBtn = document.createElement('button');
    exportBtn.className = 'btn ghost debug-export';
    exportBtn.textContent = '⬇︎ Export debug report';
    exportBtn.addEventListener('click', () => void exportReport());
    this.el.appendChild(exportBtn);

    // Occlusion toggle — for A/B checking visuals vs FPS on heavy scans.
    const occBtn = document.createElement('button');
    occBtn.className = 'btn ghost debug-export';
    const occLabel = () => `🧱 Occlusion: ${isOccluderEnabled() ? 'on' : 'off'}`;
    occBtn.textContent = occLabel();
    occBtn.addEventListener('click', () => {
      setOccluderEnabled(!isOccluderEnabled());
      occBtn.textContent = occLabel();
    });
    this.el.appendChild(occBtn);

    // VPS/FPS on-screen readout toggle — same "on"/"off" pattern as
    // Occlusion above, controls the always-on-screen "N FPS · Localization
    // N%" line (StatsHud.ts) rather than anything inside this panel.
    const statsBtn = document.createElement('button');
    statsBtn.className = 'btn ghost debug-export';
    const statsLabel = () => `📶 VPS/FPS: ${isStatsHudVisible() ? 'on' : 'off'}`;
    statsBtn.textContent = statsLabel();
    statsBtn.addEventListener('click', () => {
      const next = !isStatsHudVisible();
      setStatsHudVisible(next);
      statsBtn.textContent = statsLabel();
      this.onToggleStatsHud?.(next);
    });
    this.el.appendChild(statsBtn);

    this.toggleButton = document.createElement('button');
    this.toggleButton.className = 'icon-btn debug-toggle';
    this.toggleButton.textContent = '🐞';
    this.toggleButton.addEventListener('click', () => {
      this.visible = !this.visible;
      this.el.classList.toggle('hidden', !this.visible);
      if (this.visible) this.render();
    });
  }

  setState(s: LocalizationState): void {
    this.state = s;
    this.render();
  }

  setPose(p: PoseSnapshot): void {
    this.pose = p;
    this.render();
  }

  setRoute(route: Route | null, nextIdx = 0): void {
    this.route = route;
    this.nextIdx = nextIdx;
    this.render();
  }

  setNextIndex(i: number): void {
    this.nextIdx = i;
    this.render();
  }

  setLastLocalization(info: string): void {
    this.lastResultInfo = info;
    this.render();
  }

  private render(): void {
    if (!this.visible) return;
    const f = (n: number) => n.toFixed(2);
    const lines: string[] = [getVersionLine(), `state: ${this.state}`];
    if (this.lastResultInfo) lines.push(`vps:   ${this.lastResultInfo}`);
    if (this.pose) {
      const m = this.pose.mapPosition;
      lines.push(
        `map:   x ${f(m.x)}  y ${f(m.y)}  z ${f(m.z)}`,
        `hdg:   ${((this.pose.headingRad * 180) / Math.PI).toFixed(0)}°`,
      );
    }
    if (this.route) {
      const n = this.route.nodes;
      lines.push(
        `dest:  ${this.route.destination.name}`,
        // Long navmesh routes: elide the middle instead of flooding the panel
        `nodes: ${n.length <= 6 ? n.map((x) => x.id).join(' → ') : `${n[0].id} → … (${n.length}) … → ${n[n.length - 1].id}`}`,
        `next:  ${n[this.nextIdx]?.id ?? '(POI)'}`,
      );
    }
    lines.push('— telemetry —', ...getSummaryLines(), getVoiceDebugLine());
    this.body.textContent = lines.join('\n');
  }
}
